/**
 * Alta y edición de una habitación.
 *
 * El ESTADO no está en el formulario a propósito, ni al crear ni al editar: `updateUnitSchema`
 * lo excluye en el servidor porque una habitación pasa a ocupada cuando se activa un contrato, no
 * cuando alguien lo marca. Un desplegable «ocupada / libre» aquí sería un botón para mentirle
 * a la ocupación y a todos los indicadores que salen de ella.
 *
 * La renta que se pide es la de REFERENCIA, que no es la del contrato: el asistente la usa
 * como valor de partida y a partir de ahí manda lo que se firmó.
 */
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { UnitDto } from '@naar/shared';
import { ApiError, api } from '../../api/client';
import { Button } from '../../components/Button';
import { Drawer } from '../../components/overlay/Drawer';
import {
  Checkbox,
  Field,
  FormRow,
  Input,
  MoneyInput,
  Select,
  Textarea,
} from '../../components/form/Field';
import { toast } from '../../components/feedback/Toaster';

const UNIT_TYPE_OPTIONS = [
  { value: 'habitacion', label: 'Habitación' },
  { value: 'apartamento', label: 'Apartamento' },
  { value: 'completo', label: 'Completo' },
];

interface FormState {
  code: string;
  unitType: string;
  baseRent: string;
  areaM2: string;
  bedrooms: string;
  bathrooms: string;
  floor: string;
  orientation: string;
  hasPrivateBathroom: boolean;
  furnished: boolean;
  notes: string;
}

const EMPTY: FormState = {
  code: '',
  unitType: 'habitacion',
  baseRent: '',
  areaM2: '',
  bedrooms: '',
  bathrooms: '',
  floor: '',
  orientation: '',
  hasPrivateBathroom: false,
  furnished: false,
  notes: '',
};

function fromUnit(unit: UnitDto): FormState {
  return {
    code: unit.code,
    unitType: unit.unitType,
    baseRent: unit.baseRent,
    areaM2: unit.areaM2 === null ? '' : String(unit.areaM2),
    bedrooms: unit.bedrooms === null ? '' : String(unit.bedrooms),
    bathrooms: unit.bathrooms === null ? '' : String(unit.bathrooms),
    floor: unit.floor ?? '',
    orientation: unit.orientation ?? '',
    hasPrivateBathroom: unit.hasPrivateBathroom ?? false,
    furnished: unit.furnished ?? false,
    notes: unit.notes ?? '',
  };
}

interface UnitFormDrawerProps {
  open: boolean;
  propertyId: string;
  /** Si viene, el panel edita en vez de crear. */
  unit?: UnitDto;
  onClose: () => void;
  onSaved: () => void;
}

export function UnitFormDrawer({ open, propertyId, unit, onClose, onSaved }: UnitFormDrawerProps) {
  const editing = Boolean(unit);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setForm(unit ? fromUnit(unit) : EMPTY);
    setDirty(false);
    setFieldErrors({});
  }, [open, unit]);

  function patch(next: Partial<FormState>) {
    setForm((current) => ({ ...current, ...next }));
    setDirty(true);
  }

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        code: form.code.trim(),
        unitType: form.unitType,
        baseRent: form.baseRent || '0',
        areaM2: form.areaM2 ? Number(form.areaM2) : null,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
        floor: form.floor.trim() || null,
        orientation: form.orientation.trim() || null,
        hasPrivateBathroom: form.hasPrivateBathroom,
        furnished: form.furnished,
        notes: form.notes.trim() || null,
      };
      return editing ? api.updateUnit(unit!.id, body) : api.createUnit(propertyId, body);
    },
    onSuccess: () => {
      onSaved();
      toast.success(editing ? 'Habitación actualizada.' : `«${form.code.trim()}» añadida.`);
      setDirty(false);
      onClose();
    },
    onError: (error: unknown) => {
      if (!(error instanceof ApiError)) {
        toast.error('No se ha podido guardar.');
        return;
      }

      const errors: Record<string, string> = {};
      for (const detail of error.fieldErrors) errors[detail.path] = detail.message;

      // El código es único dentro del inmueble: el choque se explica en su campo.
      if (error.code === 'DUPLICATE_UNIT_CODE') {
        errors.code = 'Ya hay una habitación con este código en el inmueble.';
      }

      setFieldErrors(errors);
      if (Object.keys(errors).length === 0) toast.error(error.message);
    },
  });

  const valid = form.code.trim().length > 0 && Number(form.baseRent) > 0;

  return (
    <Drawer
      open={open}
      title={editing ? 'Editar habitación' : 'Nueva habitación'}
      subtitle={
        editing
          ? unit?.code
          : 'Una habitación por cada espacio que se alquile por separado: una habitación, un apartamento o el inmueble completo.'
      }
      onClose={onClose}
      dirty={dirty}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
            {save.isPending ? 'Guardando…' : editing ? 'Guardar cambios' : 'Añadir habitación'}
          </Button>
        </>
      }
    >
      <FormRow>
        <Field
          label="Código"
          required
          error={fieldErrors.code}
          help="Es como se identifica al asignarla a un inquilino: «Hab. 2», «Bajo A»…"
        >
          <Input
            value={form.code}
            onChange={(event) => patch({ code: event.target.value })}
            placeholder="Hab. 1"
            maxLength={40}
            invalid={Boolean(fieldErrors.code)}
          />
        </Field>
        <Field label="Tipo" required>
          <Select
            options={UNIT_TYPE_OPTIONS}
            value={form.unitType}
            onChange={(event) => patch({ unitType: event.target.value })}
          />
        </Field>
      </FormRow>

      <Field
        label="Renta de referencia"
        required
        error={fieldErrors.baseRent}
        help="Se propone al asignar la habitación como punto de partida; el contrato manda."
      >
        <MoneyInput
          value={form.baseRent}
          onValueChange={(value) => patch({ baseRent: value })}
          suffix="€"
          invalid={Boolean(fieldErrors.baseRent)}
        />
      </Field>

      <FormRow>
        <Field label="Superficie" optional error={fieldErrors.areaM2}>
          <Input
            value={form.areaM2}
            onChange={(event) => patch({ areaM2: event.target.value })}
            numeric
            inputMode="decimal"
            suffix="m²"
            invalid={Boolean(fieldErrors.areaM2)}
          />
        </Field>
        <Field label="Dormitorios" optional>
          <Input
            value={form.bedrooms}
            onChange={(event) => patch({ bedrooms: event.target.value })}
            numeric
            inputMode="numeric"
          />
        </Field>
        <Field label="Baños" optional>
          <Input
            value={form.bathrooms}
            onChange={(event) => patch({ bathrooms: event.target.value })}
            numeric
            inputMode="numeric"
          />
        </Field>
      </FormRow>

      <FormRow>
        <Field label="Planta" optional>
          <Input
            value={form.floor}
            onChange={(event) => patch({ floor: event.target.value })}
            placeholder="3º"
            maxLength={20}
          />
        </Field>
        <Field label="Orientación" optional>
          <Input
            value={form.orientation}
            onChange={(event) => patch({ orientation: event.target.value })}
            placeholder="Exterior, sur"
            maxLength={30}
          />
        </Field>
      </FormRow>

      <Checkbox
        checked={form.hasPrivateBathroom}
        onChange={(event) => patch({ hasPrivateBathroom: event.target.checked })}
        label="Baño privado"
      />
      <Checkbox
        checked={form.furnished}
        onChange={(event) => patch({ furnished: event.target.checked })}
        label="Amueblada"
      />

      <Field label="Notas" optional>
        <Textarea
          value={form.notes}
          onChange={(event) => patch({ notes: event.target.value })}
          rows={3}
          placeholder="Lo que convenga recordar de esta habitación."
        />
      </Field>
    </Drawer>
  );
}
